"use client";

import Link from "next/link";
import { User, MapPin, Star, Shield, ChevronRight } from "lucide-react";
import { UserProfile } from "@/data/profile/types";

interface SellerCardProps {
  sellerProfile: UserProfile;
  sellerUid: string;
  reviewCount?: number;
  rating?: number;
  isOnline?: boolean;
}

export function SellerCard({
  sellerProfile,
  sellerUid,
  reviewCount = 0,
  rating = 0,
  isOnline = false,
}: SellerCardProps) {
  const isNewSeller = reviewCount === 0;

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">판매자 정보</h2>
        <Link
          href={`/profile/${sellerUid}`}
          className="text-sm text-[#6C4DE6] hover:text-[#5A3FE8] font-medium flex items-center gap-1"
        >
          프로필 보기
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="flex items-center gap-4">
        {/* 프로필 이미지 */}
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center border-2 border-gray-200">
            {(sellerProfile.profileImage || sellerProfile.photoURL || sellerProfile.avatar) ? (
              <img
                src={sellerProfile.profileImage || sellerProfile.photoURL || sellerProfile.avatar}
                alt={sellerProfile.nickname || "판매자"}
                className="object-cover w-full h-full"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const placeholder = target.parentElement?.querySelector('.profile-placeholder') as HTMLElement;
                  if (placeholder) {
                    placeholder.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div className={`w-full h-full flex items-center justify-center text-xl text-gray-400 profile-placeholder ${(sellerProfile.profileImage || sellerProfile.photoURL || sellerProfile.avatar) ? 'hidden' : ''}`}>
              {sellerProfile.nickname?.charAt(0)?.toUpperCase() || "U"}
            </div>
          </div>
          {/* 온라인 여부 표시 */}
          {isOnline && (
            <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
          )}
        </div>

        {/* 판매자 정보 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-gray-900 text-lg">
              {sellerProfile.nickname || "판매자"}
            </h3>
            {sellerProfile.grade && (
              <span className="px-2 py-0.5 bg-[#F4F0FF] text-[#6C4DE6] rounded-full text-xs font-semibold">
                {sellerProfile.grade}등급
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-600">
            {isNewSeller ? (
              <span className="px-2 py-0.5 bg-orange-50 text-orange-600 rounded-full text-xs font-medium">
                첫 판매자 • 새 계정
              </span>
            ) : (
              <>
                {rating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span>{rating.toFixed(1)}</span>
                    <span className="text-gray-400">({reviewCount})</span>
                  </div>
                )}
                {sellerProfile.region && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{sellerProfile.region}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* 신뢰 요소 */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Shield className="w-4 h-4 text-[#6C4DE6]" />
          <span>톤마켓 인증 판매자</span>
        </div>
      </div>
    </div>
  );
}

